import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select'
import { PanelComponent } from '@components/core/panel/panel.component';
import { CertProService } from '@services/core/certpro.service';
import { EntityService } from '@services/core/entity.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-record-certpro',
  templateUrl: './record.component.html',
  styleUrls: ["./record.component.scss"],
  imports: [CommonModule, PanelComponent, FormsModule, NgbTooltipModule, NgSelectComponent],
  providers: [CertProService, EntityService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class CertproRecordsComponent implements OnInit {

  private certProService = inject(CertProService)
  private entityService = inject(EntityService)

  private today = new Date()
  protected records
  protected entity
  protected period
  protected history = true
  protected reviewCertpro = false
  protected closeResult = ""
  protected periodSelected
  protected groupSelected
  protected years = []
  protected groups = []
  protected entities = []
  private destroyRef = inject(DestroyRef)
  private router = inject(Router)



  ngOnInit(): void {
    this.periodSelected = this.today.getFullYear() + '-' + (this.today.getMonth() + 1 < 10 ? '0' + (this.today.getMonth() + 1) : (this.today.getMonth() + 1))
    this.getTypeCertPro()
  }

  getEntities() {
    this.entityService.getEntitiesGroup(this.groupSelected).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.entities = res
      this.getPeriod()
    })
  }

  getPeriod() {
    this.certProService.getPeriods(this.periodSelected, this.groupSelected).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.records = res
      this.entities.map(entity => entity.record = this.records.find(record => record.entityId === entity.id))
    })
  }

  getTypeCertPro() {
    this.entityService.getGroupsEntities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.groups = res
      this.groupSelected = res[0]
      this.getEntities()
    })
  }

  openPeriod() {
    Swal.fire({
      title: `¿Esta seguro de abrir el periodo ${this.periodSelected.split('-')[1]}-${this.periodSelected.split('-')[0]}?`,
      text: `Se dará apertura a las entidades de: ${this.groupSelected.name}`,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      html: `
      <div class='text-start'>
      <span>Se dará apertura a las entidades de: ${this.groupSelected.name}.</span><br>
      <span class='mt-4'>Ingrese la fecha de corte del periodo:</span>
      <input type="date" id="periodClose" class="form-control mt-2" placeholder='Fecha de cierre'>
      </div>`,
      preConfirm: async () => {
        const date = (document.getElementById('periodClose') as HTMLInputElement).value;
        return date
      }

    }).then((result) => {
      if (result.isConfirmed) {
        this.certProService.openPeriod(this.groupSelected.id, result.value, this.periodSelected).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
          this.getPeriod()
        })
      }
    })

  }

  reopen(entity) {
    Swal.fire({
      title: 'Modificar fecha límite',
      html: `
      <div class='text-start'>
      <span>Nueva fecha de cierre para <strong>${entity.code}</strong>:</span>
      <input type="date" id="newExpiration" class="form-control mt-2"
        value="${entity.record?.expiration ? entity.record.expiration.split('T')[0] : ''}">
      </div>`,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: async () => {
        const date = (document.getElementById('newExpiration') as HTMLInputElement).value;
        if (!date) {
          Swal.showValidationMessage('Debe seleccionar una fecha');
        }
        return date
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.certProService.updatePeriod(entity.record.id, undefined, undefined, result.value)
          .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.getPeriod()
            Swal.fire('Actualizado', 'Fecha límite modificada correctamente', 'success')
          })
      }
    })
  }

  reset(entity) {
    const currentExp = entity.record?.expiration ? entity.record.expiration.split('T')[0] : '';
    Swal.fire({
      title: '¿Restablecer formulario?',
      html: `
        <div class="text-start">
          <p>Se eliminarán <strong>todos los archivos</strong> de la nube y se borrarán las respuestas de <strong>${entity.code}</strong>.</p>
          <p class="text-danger mb-2">Esta acción no se puede deshacer.</p>
          <hr>
          <label class="fw-500">Nueva fecha límite:</label>
          <input type="date" id="resetExpiration" class="form-control mt-1" value="${currentExp}">
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, restablecer',
      cancelButtonText: 'Cancelar',
      preConfirm: async () => {
        const date = (document.getElementById('resetExpiration') as HTMLInputElement).value;
        if (!date) {
          Swal.showValidationMessage('Debe seleccionar una fecha límite');
        }
        return date
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.certProService.resetPeriod(entity.record.id, result.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.getPeriod()
            Swal.fire('Restablecido', 'Formulario restablecido correctamente', 'success')
          },
          error: (err) => {
            Swal.fire('Error', err.error?.message || 'No se pudo restablecer el formulario', 'error')
          }
        })
      }
    })
  }

  review(evaluation) {
    this.router.navigate(['/cerpro'], {
      queryParams: {
        period: evaluation.record.period,
        entity: evaluation.record.entityId,
        review: true
      }
    })
  }

  reload() {
    this.getPeriod()
    this.history = true
    this.reviewCertpro = false
  }

} 
